<script lang="ts">
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import { _ } from 'svelte-i18n';

	const personas = [
		{
			id: 'freelancer',
			icon: 'user',
			color: 'brand'
		},
		{
			id: 'agency',
			icon: 'users',
			color: 'secondary'
		},
		{
			id: 'startup',
			icon: 'zap',
			color: 'accent'
		}
	];
</script>

<section class="who-is-this-for">
	<div class="container">
		<div class="header">
			<span class="badge">{$_('whoIsThisFor.badge')}</span>
			<h2 class="title">{$_('whoIsThisFor.title')}</h2>
			<p class="subtitle">{$_('whoIsThisFor.subtitle')}</p>
		</div>

		<div class="personas-grid">
			{#each personas as persona, index}
				<Card
					hoverable
					class="persona-card"
					style="--delay: {index * 0.15}s; --color: var(--{persona.color});"
				>
					<div class="persona-icon">
						<Icon name={persona.icon} size={32} />
					</div>
					<h3 class="persona-title">{$_(`whoIsThisFor.personas.${persona.id}.title`)}</h3>
					<p class="persona-description">{$_(`whoIsThisFor.personas.${persona.id}.description`)}</p>
					<div class="persona-benefits">
						<div class="benefit-item">
							<Icon name="check" size={16} />
							<span>{$_(`whoIsThisFor.personas.${persona.id}.benefit1`)}</span>
						</div>
						<div class="benefit-item">
							<Icon name="check" size={16} />
							<span>{$_(`whoIsThisFor.personas.${persona.id}.benefit2`)}</span>
						</div>
						<div class="benefit-item">
							<Icon name="check" size={16} />
							<span>{$_(`whoIsThisFor.personas.${persona.id}.benefit3`)}</span>
						</div>
					</div>
				</Card>
			{/each}
		</div>
	</div>
</section>

<style>
	.who-is-this-for {
		padding-block: var(--section-padding-y);
		background: var(--bg-secondary);
		position: relative;
		overflow: hidden;
	}

	.who-is-this-for::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			transparent 0%,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.02) 50%,
			transparent 100%
		);
		pointer-events: none;
	}

	.container {
		position: relative;
	}

	.header {
		text-align: center;
		max-width: 800px;
		margin-inline: auto;
		margin-bottom: var(--space-16);
	}

	.badge {
		display: inline-block;
		padding: var(--space-1) var(--space-3);
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1);
		color: var(--text-brand);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
		animation: fadeInUp 0.6s ease-out;
	}

	.title {
		margin-bottom: var(--space-4);
		color: var(--text-primary);
		animation: fadeInUp 0.6s ease-out 0.1s backwards;
	}

	.subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		animation: fadeInUp 0.6s ease-out 0.2s backwards;
	}

	.personas-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-8);
		margin-bottom: var(--space-12);
	}

	:global(.persona-card) {
		padding: var(--space-8);
		animation: fadeInUp 0.6s ease-out var(--delay, 0s) backwards;
		border-left: 3px solid var(--color, var(--brand-primary));
	}

	.persona-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 64px;
		height: 64px;
		margin-bottom: var(--space-4);
		background: linear-gradient(
			135deg,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1) 0%,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05) 100%
		);
		border-radius: var(--radius-lg);
		color: var(--color, var(--text-brand));
		transition: var(--transition-all);
	}

	:global(.persona-card:hover) .persona-icon {
		transform: scale(1.1);
		background: linear-gradient(
			135deg,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.15) 0%,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1) 100%
		);
	}

	.persona-title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.persona-description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-6);
	}

	.persona-benefits {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.benefit-item {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	.benefit-item :global(svg) {
		color: hsl(120 60% 50%);
		flex-shrink: 0;
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@media (max-width: 768px) {
		.personas-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
